import { useState, useEffect } from 'react';
import { Fingerprint, Trash2, Loader2, Smartphone } from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API = '/api'; // Use relative URL for same-origin API calls

export default function BiometricSettings() {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    checkSupport();
    fetchCredentials();
  }, []);

  const checkSupport = async () => {
    if (!window.PublicKeyCredential) return;
    try {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        setSupported(true);
      } else {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setSupported(available);
      }
    } catch {
      setSupported(false);
    }
  };

  const fetchCredentials = async () => {
    try {
      const { data } = await axios.get(`${API}/webauthn/credentials`);
      setCredentials(data);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const { data } = await axios.get(`${API}/webauthn/register/options`);
      const optionsJSON = typeof data.options === 'string' ? JSON.parse(data.options) : data.options;
      const sessionId = data.session_id;
      const credential = await startRegistration({ optionsJSON });
      await axios.post(`${API}/webauthn/register/verify?session_id=${encodeURIComponent(sessionId)}`, credential);
      
      // Store email for biometric login (fetch from profile)
      try {
        const { data: profile } = await axios.get(`${API}/users/me`);
        if (profile?.email) {
          localStorage.setItem('biometric_email', profile.email);
        }
      } catch {}
      
      toast.success('Biometric login enabled!');
      fetchCredentials();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Biometric setup was cancelled');
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to set up biometric login');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemove = async (credentialId) => {
    try {
      await axios.delete(`${API}/webauthn/credentials/${credentialId}`);
      toast.success('Biometric credential removed');
      const updated = credentials.filter(c => c.credential_id !== credentialId);
      setCredentials(updated);
      // If no credentials left, clear biometric email from localStorage
      if (updated.length === 0) {
        localStorage.removeItem('biometric_email');
      }
    } catch {
      toast.error('Failed to remove credential');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading biometric settings...</span>
      </div>
    );
  }

  return (
    <div className="max-w-md" data-testid="biometric-settings">
      <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
        <Fingerprint className="w-5 h-5" />
        Biometric Login
      </h3>
      <p className="text-sm text-muted-foreground mb-4">
        Use Face ID, Touch ID, or fingerprint to sign in quickly without your password.
      </p>

      {!supported && (
        <div className="bg-muted/50 rounded-xl p-4 text-sm text-muted-foreground">
          <Smartphone className="w-4 h-4 inline mr-1" />
          Biometric login is not available on this device.
        </div>
      )}

      {supported && credentials.length === 0 && (
        <Button
          onClick={handleEnroll}
          disabled={enrolling}
          className="rounded-full"
          data-testid="enable-biometric-settings-btn"
        >
          {enrolling ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Setting up...
            </>
          ) : (
            <>
              <Fingerprint className="w-4 h-4 mr-2" />
              Enable Biometric Login
            </>
          )}
        </Button>
      )}

      {supported && credentials.length > 0 && (
        <div className="space-y-3">
          {credentials.map((cred, index) => (
            <div
              key={cred.credential_id || index}
              className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl"
              data-testid={`biometric-credential-${index}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <Fingerprint className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-800">Biometric Login Enabled</p>
                  <p className="text-xs text-green-600">
                    {cred.device_type || 'Device'} — Added {cred.created_at ? new Date(cred.created_at).toLocaleDateString() : 'recently'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleRemove(cred.credential_id)}
                className="text-red-400 hover:text-red-600 p-1"
                title="Remove credential"
                data-testid={`remove-biometric-${index}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
